import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { SocialChannelPlatforms, type SocialChannelPlatform } from '@/api/types/Common'

interface SocialChannelFormType {
  type: SocialChannelPlatform
  handle?: string
  sourceUrl?: string
}

const useSocialChannelForm = () => {
  const defaultValues = useCallback((): SocialChannelFormType => ({
    type: 'other',
    handle: '',
    sourceUrl: '',
  }), [])

  const validationSchema = useMemo(() => z.object({
    type: z.enum(SocialChannelPlatforms),
    handle: z.string().optional(),
    sourceUrl: z
      .string()
      .trim()
      .optional()
      .refine(
        val => !val || val === '' || /^https?:\/\/.+/i.test(val),
        { message: 'Invalid URL' }
      ),
  }).refine(
    data => {
      const handleEmpty = !data.handle || data.handle.trim() === ''
      const urlEmpty = !data.sourceUrl || data.sourceUrl.trim() === ''
      if (handleEmpty && urlEmpty) return false
      return true
    },
    {
      message: 'URL is required',
      path: ['sourceUrl'],
    }
  ), [])

  const form = useForm<SocialChannelFormType>({
    defaultValues: defaultValues(),
    resolver: zodResolver(validationSchema),
  })

  const resetForm = useCallback(() => {
    form.reset(defaultValues())
  }, [form, defaultValues])

  return {
    form,
    resetForm,
  }
}

export default useSocialChannelForm
